import * as THREE from 'three';
import { SeededRandom } from '@/core/Random';
import type { FloraGeometryForm } from './FloraDefinition';
import type { GrowthState } from './FloraGrowthModel';
import {
  MeshBuilder,
  blobGeometry,
  blobMatrix,
  limbPath,
  taperedLimb,
  verticalShade,
  type LimbNode,
} from './GeometryBuilder';

/**
 * Procedural flora geometry (spec §8.3, §9). Builds recognizable, distinctly-silhouetted plant
 * geometry parameterized by growth state and a deterministic `visualSeed`. This produces the
 * *temporary editable representation* of a plant; batched instancing comes in Milestone 6.
 *
 * Geometry is a pure function of (form, growthState, visualSeed, detail) so it is deterministic and
 * testable (spec §3.3).
 *
 * Every form emits up to three parts so the season model can drive them independently:
 * - `structure` — woody trunk/branches/stems, revealed as deciduous foliage thins in winter.
 * - `foliage` — leaf mass, recolored continuously by {@link resolveSeasonalAppearance}.
 * - `accent` — blossoms, seed heads, berries; shown only inside a bloom/fruit window.
 */

export interface FloraGeometryParts {
  /** Solid parts (trunk/branches/stems). */
  structure: THREE.BufferGeometry | null;
  /** Foliage/canopy parts (seasonally colored). */
  foliage: THREE.BufferGeometry | null;
  /** Blossom / seed-head / berry parts (shown during bloom or fruit windows). */
  accent: THREE.BufferGeometry | null;
}

/** Geometric detail tier. Milestone 6.2 swaps these by camera distance; `high` is the editor default. */
export type FloraGeometryDetail = 'high' | 'medium' | 'low';

export interface FloraGeometryOptions {
  detail?: FloraGeometryDetail;
  /** Build blossom/seed-head geometry. Skipped for plants with no floral interest. */
  accents?: boolean;
}

interface DetailProfile {
  blobWidth: number;
  blobHeight: number;
  radial: number;
  limbSegments: number;
  /** Scales element counts (branches, blades, clusters). */
  density: number;
  /** Sub-branches are a high-detail-only refinement. */
  subBranches: boolean;
}

const PROFILES: Record<FloraGeometryDetail, DetailProfile> = {
  high: { blobWidth: 10, blobHeight: 7, radial: 6, limbSegments: 6, density: 1, subBranches: true },
  medium: { blobWidth: 8, blobHeight: 5, radial: 5, limbSegments: 4, density: 0.7, subBranches: false },
  low: { blobWidth: 6, blobHeight: 4, radial: 4, limbSegments: 3, density: 0.45, subBranches: false },
};

function clusterBlob(profile: DetailProfile, seed: number, roughness: number): THREE.BufferGeometry {
  return blobGeometry(seed, {
    roughness,
    widthSegments: profile.blobWidth,
    heightSegments: profile.blobHeight,
  });
}

/** Scale an element count by the detail profile, never dropping below a legible minimum. */
function count(nominal: number, profile: DetailProfile, minimum = 2): number {
  return Math.max(minimum, Math.round(nominal * profile.density));
}

/* ------------------------------------------------------------------ */
/* Trees                                                               */
/* ------------------------------------------------------------------ */

interface BranchTip {
  position: THREE.Vector3;
  direction: THREE.Vector3;
}

/**
 * Build a trunk with a root flare and a lean, plus a whorl of primary branches reaching into the
 * canopy volume. Returns the branch tips so the canopy can be hung off them rather than floating as
 * a detached ball — the single change that most improves how a tree reads.
 */
function woodyFrame(
  builder: MeshBuilder,
  state: GrowthState,
  rng: SeededRandom,
  profile: DetailProfile,
  options: { trunkHeight: number; canopyRadius: number; canopyTop: number; branches: number },
): BranchTip[] {
  const { trunkHeight, canopyRadius, canopyTop } = options;
  const shade = verticalShade(0, canopyTop, 0.7, 1.05);

  const leanAngle = rng.range(0, Math.PI * 2);
  const leanAmount = rng.range(0.02, 0.07);
  const trunkTipRadius = state.trunkRadiusMeters * rng.range(0.45, 0.6);

  const trunk = limbPath({
    base: new THREE.Vector3(0, 0, 0),
    direction: new THREE.Vector3(0, 1, 0),
    length: trunkHeight,
    baseRadius: state.trunkRadiusMeters,
    tipRadius: trunkTipRadius,
    segments: profile.limbSegments,
    curl: new THREE.Vector3(Math.cos(leanAngle) * leanAmount, 0, Math.sin(leanAngle) * leanAmount),
    flare: rng.range(1.35, 1.7),
  });
  builder.add(taperedLimb(trunk, profile.radial), { shade });

  const crown = trunk[trunk.length - 1]?.position ?? new THREE.Vector3(0, trunkHeight, 0);
  const branchCount = count(options.branches, profile, 3);
  const tips: BranchTip[] = [];
  const startAngle = rng.range(0, Math.PI * 2);

  for (let i = 0; i < branchCount; i += 1) {
    // Golden-angle spacing with jitter keeps whorls from looking mechanically radial.
    const angle = startAngle + i * 2.399963 + rng.range(-0.25, 0.25);
    const spreadOut = rng.range(0.55, 0.95);
    const direction = new THREE.Vector3(
      Math.cos(angle) * spreadOut,
      rng.range(0.6, 1.05),
      Math.sin(angle) * spreadOut,
    ).normalize();
    const length = canopyRadius * rng.range(0.7, 1.0) + (canopyTop - trunkHeight) * 0.35;
    const base = crown.clone().addScaledVector(new THREE.Vector3(0, -1, 0), rng.range(0, trunkHeight * 0.18));
    const branch = limbPath({
      base,
      direction,
      length,
      baseRadius: trunkTipRadius * rng.range(0.42, 0.6),
      tipRadius: trunkTipRadius * 0.14,
      segments: Math.max(3, profile.limbSegments - 1),
      // Branches reach up then level off under their own weight.
      curl: new THREE.Vector3(direction.x * 0.18, rng.range(-0.22, 0.06), direction.z * 0.18),
    });
    builder.add(taperedLimb(branch, Math.max(4, profile.radial - 1)), { shade });

    const tip = branch[branch.length - 1] ?? { position: base, radius: 0 };
    const previous = branch[branch.length - 2] ?? tip;
    const tipDirection = tip.position.clone().sub(previous.position).normalize();
    tips.push({ position: tip.position.clone(), direction: tipDirection });

    if (profile.subBranches) {
      const forkFrom = branch[Math.max(1, Math.floor(branch.length * 0.55))] ?? tip;
      for (let s = 0; s < 2; s += 1) {
        const forkAngle = angle + (s === 0 ? 0.8 : -0.8) + rng.range(-0.2, 0.2);
        const forkDirection = new THREE.Vector3(
          Math.cos(forkAngle) * 0.8,
          rng.range(0.5, 0.9),
          Math.sin(forkAngle) * 0.8,
        ).normalize();
        const fork = limbPath({
          base: forkFrom.position.clone(),
          direction: forkDirection,
          length: length * rng.range(0.35, 0.55),
          baseRadius: forkFrom.radius * 0.7,
          tipRadius: forkFrom.radius * 0.15,
          segments: 3,
          curl: new THREE.Vector3(0, rng.range(-0.15, 0.05), 0),
        });
        builder.add(taperedLimb(fork, 4), { shade });
        const forkTip = fork[fork.length - 1];
        if (forkTip) {
          tips.push({ position: forkTip.position.clone(), direction: forkDirection });
        }
      }
    }
  }

  return tips;
}

function roundCanopyTree(
  state: GrowthState,
  rng: SeededRandom,
  profile: DetailProfile,
  accents: boolean,
): FloraGeometryParts {
  const height = Math.max(0.3, state.heightMeters);
  const canopyRadius = Math.max(0.12, state.spreadMeters / 2);
  const trunkHeight = Math.max(0.15, state.canopyBaseMeters > 0 ? state.canopyBaseMeters * 1.15 : height * 0.35);
  const canopyBottom = trunkHeight * 0.72;
  const canopyTop = height;
  const canopyHeight = Math.max(0.15, canopyTop - canopyBottom);

  const structure = new MeshBuilder();
  const tips = woodyFrame(structure, state, rng, profile, {
    trunkHeight,
    canopyRadius,
    canopyTop: canopyTop - canopyHeight * 0.25,
    branches: 5,
  });

  const foliage = new MeshBuilder();
  const shade = verticalShade(canopyBottom, canopyTop);
  const clusterRadius = canopyRadius * (0.46 - 0.02 * Math.min(tips.length, 8));

  // One cluster per branch tip, pushed slightly past the tip so leaves sit outside the wood.
  for (const tip of tips) {
    const center = tip.position.clone().addScaledVector(tip.direction, clusterRadius * 0.35);
    const radius = clusterRadius * rng.range(0.85, 1.3) * (0.7 + 0.3 * state.foliageDensity);
    foliage.add(clusterBlob(profile, rng.int(1, 9999), 0.26), {
      matrix: blobMatrix(
        center,
        new THREE.Vector3(radius, radius * rng.range(0.72, 0.95), radius),
        rng.range(0, Math.PI * 2),
      ),
      shade,
    });
  }

  // A core mass so the crown reads solid from a distance instead of as separate balls.
  const coreRadius = canopyRadius * rng.range(0.52, 0.66);
  foliage.add(clusterBlob(profile, rng.int(1, 9999), 0.18), {
    matrix: blobMatrix(
      new THREE.Vector3(0, canopyBottom + canopyHeight * 0.55, 0),
      new THREE.Vector3(coreRadius, canopyHeight * 0.42, coreRadius),
      rng.range(0, Math.PI * 2),
    ),
    shade,
  });

  const accent = accents
    ? scatterAccents(rng, {
        count: count(24, profile, 10),
        center: new THREE.Vector3(0, canopyBottom + canopyHeight * 0.55, 0),
        radius: new THREE.Vector3(canopyRadius * 0.82, canopyHeight * 0.42, canopyRadius * 0.82),
        // Tree blossoms are clusters a hand-span across, not fruit — scale gently with the canopy
        // and cap hard, or a mature crown ends up hung with beach balls.
        size: THREE.MathUtils.clamp(canopyRadius * 0.022, 0.02, 0.085),
      })
    : null;

  return { structure: structure.build(), foliage: foliage.build(), accent };
}

function conicalTree(
  state: GrowthState,
  rng: SeededRandom,
  profile: DetailProfile,
  accents: boolean,
): FloraGeometryParts {
  const height = Math.max(0.3, state.heightMeters);
  const radius = Math.max(0.1, state.spreadMeters / 2);
  const canopyBottom = Math.min(height * 0.22, state.canopyBaseMeters * 0.45);
  const canopyHeight = Math.max(0.2, height - canopyBottom);

  // A conifer keeps a single central leader all the way to the tip.
  const structure = new MeshBuilder();
  const trunk = limbPath({
    base: new THREE.Vector3(0, 0, 0),
    direction: new THREE.Vector3(0, 1, 0),
    length: height * 0.97,
    baseRadius: state.trunkRadiusMeters,
    tipRadius: state.trunkRadiusMeters * 0.08,
    segments: profile.limbSegments,
    curl: new THREE.Vector3(rng.range(-0.02, 0.02), 0, rng.range(-0.02, 0.02)),
    flare: rng.range(1.4, 1.8),
  });
  structure.add(taperedLimb(trunk, profile.radial), {
    shade: verticalShade(0, height, 0.72, 1.06),
  });

  const foliage = new MeshBuilder();
  const shade = verticalShade(canopyBottom, height, 0.58, 1.14);
  const tiers = count(9, profile, 4);
  const tierHeight = (canopyHeight / tiers) * 2.05;

  for (let i = 0; i < tiers; i += 1) {
    const t = i / (tiers - 1 || 1);
    const y = canopyBottom + canopyHeight * t;
    // Concave taper: the classic spruce profile is not a straight-sided cone.
    const tierRadius = radius * Math.pow(1 - t, 0.78) * rng.range(0.88, 1.12) * (0.75 + 0.25 * state.foliageDensity);
    if (tierRadius < 0.01) continue;
    const cone = new THREE.ConeGeometry(tierRadius, tierHeight * rng.range(0.85, 1.15), 7, 1, false);
    foliage.add(cone, {
      matrix: new THREE.Matrix4().compose(
        new THREE.Vector3(rng.range(-0.02, 0.02) * radius, y + tierHeight * 0.35, rng.range(-0.02, 0.02) * radius),
        new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), rng.range(0, Math.PI * 2)),
        new THREE.Vector3(1, 1, 1),
      ),
      shade,
    });
  }

  // Leader spike so the silhouette ends in a point rather than a flat tier.
  foliage.add(new THREE.ConeGeometry(radius * 0.13, canopyHeight * 0.16, 6), {
    matrix: new THREE.Matrix4().makeTranslation(0, height - canopyHeight * 0.06, 0),
    shade,
  });

  const accent = accents
    ? scatterAccents(rng, {
        count: count(12, profile, 5),
        center: new THREE.Vector3(0, canopyBottom + canopyHeight * 0.7, 0),
        radius: new THREE.Vector3(radius * 0.5, canopyHeight * 0.22, radius * 0.5),
        // Spruce cones run about 10 cm; elongated rather than round.
        size: THREE.MathUtils.clamp(radius * 0.03, 0.018, 0.05),
        elongate: 2.4,
      })
    : null;

  return { structure: structure.build(), foliage: foliage.build(), accent };
}

/* ------------------------------------------------------------------ */
/* Shrubs, grasses, perennials, groundcover                            */
/* ------------------------------------------------------------------ */

function moundingShrub(
  state: GrowthState,
  rng: SeededRandom,
  profile: DetailProfile,
  accents: boolean,
): FloraGeometryParts {
  const radius = Math.max(0.08, state.spreadMeters / 2);
  const height = Math.max(0.1, state.heightMeters);

  // Multi-stemmed base: what makes a shrub read as a shrub rather than a green dome.
  const structure = new MeshBuilder();
  const stems = count(6, profile, 3);
  const stemShade = verticalShade(0, height, 0.7, 1.02);
  for (let i = 0; i < stems; i += 1) {
    const angle = rng.range(0, Math.PI * 2);
    const outward = rng.range(0.3, 0.6);
    const stem = limbPath({
      base: new THREE.Vector3(Math.cos(angle) * radius * 0.1, 0, Math.sin(angle) * radius * 0.1),
      direction: new THREE.Vector3(Math.cos(angle) * outward, 1, Math.sin(angle) * outward).normalize(),
      length: height * rng.range(0.7, 0.95),
      baseRadius: Math.max(0.006, state.trunkRadiusMeters * 0.35),
      tipRadius: Math.max(0.002, state.trunkRadiusMeters * 0.1),
      segments: Math.max(3, profile.limbSegments - 2),
      curl: new THREE.Vector3(Math.cos(angle) * 0.3, -0.1, Math.sin(angle) * 0.3),
      flare: 1.3,
    });
    structure.add(taperedLimb(stem, 4), { shade: stemShade });
  }

  const foliage = new MeshBuilder();
  const shade = verticalShade(0, height, 0.55, 1.12);
  const clusters = count(9, profile, 4);
  for (let i = 0; i < clusters; i += 1) {
    const angle = i * 2.399963 + rng.range(-0.3, 0.3);
    // Bias clusters toward the outside and top so the mound domes rather than sags.
    const distance = radius * Math.sqrt(rng.range(0.05, 0.85));
    const lift = height * rng.range(0.35, 0.62) * (1 - (distance / radius) * 0.45);
    const size = radius * rng.range(0.3, 0.46) * (0.75 + 0.25 * state.foliageDensity);
    foliage.add(clusterBlob(profile, rng.int(1, 9999), 0.24), {
      matrix: blobMatrix(
        new THREE.Vector3(Math.cos(angle) * distance, lift, Math.sin(angle) * distance),
        new THREE.Vector3(size, size * rng.range(0.7, 0.95), size),
        rng.range(0, Math.PI * 2),
      ),
      shade,
    });
  }

  const accent = accents
    ? scatterAccents(rng, {
        count: count(11, profile, 5),
        center: new THREE.Vector3(0, height * 0.72, 0),
        radius: new THREE.Vector3(radius * 0.75, height * 0.16, radius * 0.75),
        size: Math.max(0.02, radius * 0.16),
        elongate: 1.5,
      })
    : null;

  return { structure: structure.build(), foliage: foliage.build(), accent };
}

function grassTuft(
  state: GrowthState,
  rng: SeededRandom,
  profile: DetailProfile,
  accents: boolean,
): FloraGeometryParts {
  const height = Math.max(0.1, state.heightMeters);
  const spread = Math.max(0.04, state.spreadMeters / 2);
  const foliage = new MeshBuilder();
  const shade = verticalShade(0, height, 0.5, 1.16);
  const blades = count(9 + Math.round(state.foliageDensity * 12), profile, 7);
  const bladeWidth = Math.max(0.004, height * 0.016);
  const tips: THREE.Vector3[] = [];

  for (let i = 0; i < blades; i += 1) {
    const angle = i * 2.399963 + rng.range(-0.4, 0.4);
    const distance = spread * Math.sqrt(rng.next()) * 0.55;
    const bladeHeight = height * rng.range(0.62, 1.05);
    const outward = rng.range(0.12, 0.42);
    // Blades arc: they leave the crown near-vertical then fall outward under their own weight.
    const path: LimbNode[] = limbPath({
      base: new THREE.Vector3(Math.cos(angle) * distance, 0, Math.sin(angle) * distance),
      direction: new THREE.Vector3(Math.cos(angle) * outward, 1, Math.sin(angle) * outward).normalize(),
      length: bladeHeight,
      baseRadius: bladeWidth * rng.range(0.8, 1.2),
      tipRadius: bladeWidth * 0.08,
      segments: Math.max(3, profile.limbSegments),
      curl: new THREE.Vector3(
        Math.cos(angle) * rng.range(0.25, 0.7),
        -rng.range(0.15, 0.5),
        Math.sin(angle) * rng.range(0.25, 0.7),
      ),
    });
    foliage.add(taperedLimb(path, 3), { shade });
    const tip = path[path.length - 1];
    if (tip) tips.push(tip.position.clone());
  }

  let accent: THREE.BufferGeometry | null = null;
  if (accents && tips.length > 0) {
    // Seed heads (panicles) ride on a subset of blade tips.
    const builder = new MeshBuilder();
    const headCount = Math.max(2, Math.round(tips.length * 0.4));
    const headShade = verticalShade(0, height, 0.8, 1.1);
    for (let i = 0; i < headCount; i += 1) {
      const tip = tips[Math.floor((i / headCount) * tips.length)];
      if (!tip) continue;
      const size = height * rng.range(0.035, 0.06);
      builder.add(clusterBlob(profile, rng.int(1, 9999), 0.3), {
        matrix: blobMatrix(tip, new THREE.Vector3(size * 0.5, size * 2.4, size * 0.5), rng.range(0, Math.PI * 2)),
        shade: headShade,
      });
    }
    accent = builder.build();
  }

  return { structure: null, foliage: foliage.build(), accent };
}

function perennialClump(
  state: GrowthState,
  rng: SeededRandom,
  profile: DetailProfile,
  accents: boolean,
): FloraGeometryParts {
  const radius = Math.max(0.06, state.spreadMeters / 2);
  const height = Math.max(0.08, state.heightMeters);
  const foliage = new MeshBuilder();
  const shade = verticalShade(0, height, 0.55, 1.12);

  // A basal rosette of leaves…
  const leaves = count(7, profile, 4);
  const mound = height * 0.42;
  for (let i = 0; i < leaves; i += 1) {
    const angle = i * 2.399963 + rng.range(-0.3, 0.3);
    const size = radius * rng.range(0.34, 0.5);
    foliage.add(clusterBlob(profile, rng.int(1, 9999), 0.26), {
      matrix: blobMatrix(
        new THREE.Vector3(
          Math.cos(angle) * radius * rng.range(0.15, 0.55),
          mound * rng.range(0.4, 0.75),
          Math.sin(angle) * radius * rng.range(0.15, 0.55),
        ),
        new THREE.Vector3(size, size * rng.range(0.5, 0.75), size),
        rng.range(0, Math.PI * 2),
      ),
      shade,
    });
  }

  // …and upright flowering stems above it.
  const stems = count(6, profile, 3);
  const stemTips: THREE.Vector3[] = [];
  for (let i = 0; i < stems; i += 1) {
    const angle = i * 2.399963 + rng.range(-0.4, 0.4);
    const lean = rng.range(0.05, 0.22);
    const path = limbPath({
      base: new THREE.Vector3(Math.cos(angle) * radius * 0.25, mound * 0.4, Math.sin(angle) * radius * 0.25),
      direction: new THREE.Vector3(Math.cos(angle) * lean, 1, Math.sin(angle) * lean).normalize(),
      length: (height - mound * 0.4) * rng.range(0.8, 1.0),
      baseRadius: Math.max(0.004, height * 0.014),
      tipRadius: Math.max(0.002, height * 0.008),
      segments: 4,
      curl: new THREE.Vector3(Math.cos(angle) * 0.1, -0.05, Math.sin(angle) * 0.1),
    });
    foliage.add(taperedLimb(path, 4), { shade });
    const tip = path[path.length - 1];
    if (tip) stemTips.push(tip.position.clone());
  }

  let accent: THREE.BufferGeometry | null = null;
  if (accents && stemTips.length > 0) {
    const builder = new MeshBuilder();
    const headShade = verticalShade(0, height, 0.85, 1.08);
    for (const tip of stemTips) {
      const size = Math.max(0.015, radius * rng.range(0.16, 0.24));
      builder.add(clusterBlob(profile, rng.int(1, 9999), 0.16), {
        matrix: blobMatrix(tip, new THREE.Vector3(size, size * 0.55, size), rng.range(0, Math.PI * 2)),
        shade: headShade,
      });
    }
    accent = builder.build();
  }

  return { structure: null, foliage: foliage.build(), accent };
}

function groundcoverMat(
  state: GrowthState,
  rng: SeededRandom,
  profile: DetailProfile,
  accents: boolean,
): FloraGeometryParts {
  const radius = Math.max(0.08, state.spreadMeters / 2);
  const height = Math.max(0.02, state.heightMeters);
  const foliage = new MeshBuilder();
  const shade = verticalShade(0, height * 1.6, 0.58, 1.1);
  const patches = count(13, profile, 6);

  for (let i = 0; i < patches; i += 1) {
    const angle = i * 2.399963 + rng.range(-0.3, 0.3);
    const distance = radius * Math.sqrt(rng.next());
    // Flattened, overlapping patches that thin out toward the edge of the mat.
    const size = radius * rng.range(0.22, 0.4) * (1 - (distance / radius) * 0.35);
    foliage.add(clusterBlob(profile, rng.int(1, 9999), 0.3), {
      matrix: blobMatrix(
        new THREE.Vector3(Math.cos(angle) * distance, height * rng.range(0.35, 0.8), Math.sin(angle) * distance),
        new THREE.Vector3(size, height * rng.range(0.5, 0.95), size),
        rng.range(0, Math.PI * 2),
      ),
      shade,
    });
  }

  const accent = accents
    ? scatterAccents(rng, {
        count: count(10, profile, 4),
        center: new THREE.Vector3(0, height * 0.95, 0),
        radius: new THREE.Vector3(radius * 0.85, height * 0.3, radius * 0.85),
        size: Math.max(0.01, radius * 0.07),
      })
    : null;

  return { structure: null, foliage: foliage.build(), accent };
}

/* ------------------------------------------------------------------ */
/* Blossoms / berries                                                  */
/* ------------------------------------------------------------------ */

interface AccentOptions {
  count: number;
  center: THREE.Vector3;
  /** Ellipsoid half-extents the accents are scattered over. */
  radius: THREE.Vector3;
  size: number;
  /** Vertical stretch — >1 makes elongated forms (cones, panicles) rather than round berries. */
  elongate?: number;
}

/** Scatter small blobs over the shell of an ellipsoid, biased outward so they read on the surface. */
function scatterAccents(rng: SeededRandom, options: AccentOptions): THREE.BufferGeometry | null {
  const builder = new MeshBuilder();
  const elongate = options.elongate ?? 1;
  for (let i = 0; i < options.count; i += 1) {
    // Fibonacci-sphere directions give even coverage without clumping.
    const y = 1 - (i / Math.max(1, options.count - 1)) * 1.7;
    const ringRadius = Math.sqrt(Math.max(0, 1 - y * y));
    const theta = i * 2.399963 + rng.range(-0.2, 0.2);
    const shell = rng.range(0.78, 1.0);
    const position = new THREE.Vector3(
      options.center.x + Math.cos(theta) * ringRadius * options.radius.x * shell,
      options.center.y + y * options.radius.y * shell,
      options.center.z + Math.sin(theta) * ringRadius * options.radius.z * shell,
    );
    const size = options.size * rng.range(0.75, 1.25);
    builder.add(blobGeometry(rng.int(1, 9999), { roughness: 0.14, widthSegments: 6, heightSegments: 4 }), {
      matrix: blobMatrix(position, new THREE.Vector3(size, size * elongate, size), rng.range(0, Math.PI * 2)),
      shade: () => 1,
    });
  }
  return builder.build();
}

/* ------------------------------------------------------------------ */

/** Build the structure + foliage + accent geometry for a plant form. */
export function buildFloraGeometry(
  form: FloraGeometryForm,
  state: GrowthState,
  visualSeed: number,
  options: FloraGeometryOptions = {},
): FloraGeometryParts {
  const rng = new SeededRandom(visualSeed);
  const profile = PROFILES[options.detail ?? 'high'];
  const accents = options.accents ?? true;

  switch (form) {
    case 'round-canopy-tree':
      return roundCanopyTree(state, rng, profile, accents);
    case 'conical-tree':
      return conicalTree(state, rng, profile, accents);
    case 'mounding-shrub':
      return moundingShrub(state, rng, profile, accents);
    case 'grass-tuft':
      return grassTuft(state, rng, profile, accents);
    case 'perennial-clump':
      return perennialClump(state, rng, profile, accents);
    case 'groundcover-mat':
      return groundcoverMat(state, rng, profile, accents);
  }
}
